import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Clock, Users, Gift, Star, Trophy, ShoppingCart, MessageCircle, Plane } from 'lucide-react'
import Header from './Header'
import Footer from './Footer'
import PaymentModal from './PaymentModal'
import { membresiaService } from '../services/sorteoService'

const RafflePage = () => {
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [customQuantity, setCustomQuantity] = useState('')
  const [customTotal, setCustomTotal] = useState(0)
  const [raffle, setRaffle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [numerosSeleccionados, setNumerosSeleccionados] = useState({})
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [relatedRaffles, setRelatedRaffles] = useState([])
  const [loadingRelated, setLoadingRelated] = useState(true)
  const navigate = useNavigate()
  const { id } = useParams()

  // Función para obtener sorteos relacionados
  const loadRelatedRaffles = async () => {
    try {
      setLoadingRelated(true)
      const sorteos = await membresiaService.getAllMembresias()
      // Filtrar sorteos activos excluyendo el sorteo actual
      const activeSorteos = sorteos
        .filter(sorteo => sorteo.status === 'active' && sorteo.id.toString() !== id)
        .slice(0, 3) // Mostrar máximo 3 sorteos relacionados
      
      setRelatedRaffles(activeSorteos)
    } catch (error) {
      console.error('Error al cargar sorteos relacionados:', error)
      setRelatedRaffles([])
    } finally {
      setLoadingRelated(false)
    }
  }

  // Función para cargar números seleccionados de premios secundarios
  const loadNumerosSeleccionados = async () => {
    try {
      const response = await fetch(`/api/sorteos/${id}/numeros-premios-secundarios`)
      if (response.ok) {
        const data = await response.json()
        setNumerosSeleccionados(data)
      }
    } catch (error) {
      console.error('Error al cargar números seleccionados:', error)
    }
  }

  useEffect(() => {
    if (id) {
      loadRaffle()
      loadNumerosSeleccionados()
      loadRelatedRaffles()
    }
  }, [id])

  const loadRaffle = async () => {
    try {
      setLoading(true)
      const sorteoData = await membresiaService.getMembresiaById(id)
      setRaffle(sorteoData)
    } catch (err) {
      console.error('Error al cargar membresía:', err)
      setError('Error al cargar la membresía')
      // Fallback a datos estáticos
      setRaffle({
        id: 1,
        name: "iPhone 15 Pro Max + AirPods Pro",
        image_url: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=800&h=600&fit=crop&crop=center",
        description: "Participa en nuestro sorteo premium y gana el último iPhone 15 Pro Max junto con los AirPods Pro. Un combo tecnológico increíble que puede ser tuyo por solo $25 por boleto.",
        amount: 25000,
        total_boletas: 100000,
        boletas_vendidas: 45230,
        end_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        status: 'active'
      })
    } finally {
      setLoading(false)
    }
  }

  // Generar números ganadores aleatorios
  const generateRandomNumbers = (count, max = 100000) => {
    const numbers = new Set()
    while (numbers.size < count) {
      numbers.add(Math.floor(Math.random() * max) + 1)
    }
    return Array.from(numbers).sort((a, b) => a - b)
  }

  const winningNumbers1M = generateRandomNumbers(20)
  const winningNumbers500K = generateRandomNumbers(50)

  // Precio por boleto desde el sorteo
  const pricePerTicket = raffle?.amount || 1000

  const packages = [
    { 
      id: 1, 
      quantity: 6, 
      price: pricePerTicket * 6, 
      discount: 6, 
      popular: false 
    },
    { 
      id: 2, 
      quantity: 12, 
      price: pricePerTicket * 12, 
      discount: 12, 
      popular: true 
    },
    { 
      id: 3, 
      quantity: 18, 
      price: pricePerTicket * 18, 
      discount: 18, 
      popular: false 
    }
  ]

  // Función para calcular el total personalizado
  const handleCustomQuantityChange = (value) => {
    const quantity = parseInt(value) || 0
    setCustomQuantity(value)
    if (raffle) {
      setCustomTotal(quantity * pricePerTicket) // Precio por boleto
    }
  }

  // Función para calcular tiempo restante
  const calculateTimeLeft = (endDate) => {
    if (!endDate) return 'Sin fecha límite'
    
    const now = new Date()
    const end = new Date(endDate)
    const diff = end - now
    
    if (diff <= 0) return 'Finalizado'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) {
      return `${days} días ${hours}h`
    } else {
      return `${hours}h`
    }
  }

  // Verificar disponibilidad de boletos
  const availableTickets = raffle ? (raffle.total_boletas - (raffle.boletas_vendidas || 0)) : 0
  const isCustomQuantityValid = customQuantity && parseInt(customQuantity) > 0 && parseInt(customQuantity) <= availableTickets

  const getProgressPercentage = () => {
    if (!raffle) return 0
    return ((raffle.boletas_vendidas || 0) / raffle.total_boletas) * 100
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-CO').format(num)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 25%, #1f2937 50%, #111827 75%, #000000 100%)'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Cargando membresía...</p>
        </div>
      </div>
    )
  }

  if (error || !raffle) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 25%, #1f2937 50%, #111827 75%, #000000 100%)'}}>
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error || 'Membresía no encontrada'}</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-white/20 text-white px-6 py-3 rounded-lg hover:bg-white/30 transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative" style={{background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 25%, #1f2937 50%, #111827 75%, #000000 100%)'}}>
      {/* Degradado adicional en esquina superior izquierda */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-black/40 via-black/20 to-transparent rounded-full blur-3xl"></div>
      
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Hero Section del Sorteo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 mb-16"
          >
            {/* Imagen del Premio */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src={raffle.image_url || "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=800&h=600&fit=crop&crop=center"} 
                  alt={raffle.name}
                  className="w-full h-96 object-cover"
                />
                <div className="absolute top-6 left-6 bg-gradient-to-r from-yellow-400 to-orange-400 text-black px-4 py-2 rounded-full text-sm font-bold z-10">
                  🏆 MEMBRESÍA
                </div>
                <div className="absolute top-6 right-6 bg-black/50 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {calculateTimeLeft(raffle.end_date)}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
              </div>
            </div>

            {/* Información del Sorteo */}
            <div className="space-y-6">
              <div>
                <div className="text-sm text-blue-400 font-semibold mb-2">Membresía Premium</div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  {raffle.name}
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {raffle.description || 'Participa en este increíble sorteo y gana premios fantásticos.'}
                </p>
              </div>

              {/* Estadísticas */}
              <div className="grid grid-cols-2 gap-6">
                <div className="card-modern p-6 text-center">
                  <div className="text-3xl font-bold text-blue-400">${formatNumber(raffle.amount)} COP</div>
                  <div className="text-sm text-gray-400">Valor Membresía</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-3xl font-bold text-orange-400">{calculateTimeLeft(raffle.end_date)}</div>
                  <div className="text-sm text-gray-400">restantes</div>
                </div>
              </div>

              {/* Progreso de Venta */}
              <div className="card-modern p-6">
                <div className="w-full bg-gray-700 rounded-full h-4 mb-3">
                  <div 
                    className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-4 rounded-full transition-all duration-300 pulse-glow"
                    style={{ width: `${getProgressPercentage()}%` }}
                  ></div>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold text-white">{Math.round(getProgressPercentage())}%</span>
                  <span className="text-gray-400 ml-2">completado</span>
                </div>
              </div>

              <div className="text-center text-gray-400">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Users className="w-5 h-5" />
                  <span>Membresía finaliza el {new Date(raffle.end_date).toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Sección de Números Ganadores */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Números <span className="gradient-text">Ganadores</span>
              </h2>
              <p className="text-xl text-gray-300">
                Si tu número está en alguna de estas listas, ¡eres ganador!
              </p>
            </div>

            {/* Premios Secundarios */}
            {raffle.premios_secundarios && raffle.premios_secundarios.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {raffle.premios_secundarios.map((premio, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                      className="card-modern p-8 relative overflow-hidden"
                    >
                      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-purple-500"></div>
                      <div className="text-center mb-6">
                        <div className="flex items-center justify-center gap-3 mb-3">
                          {(() => {
                            // Colores para el indicador del premio
                            const coloresIndicador = [
                              'text-blue-400',
                              'text-green-400', 
                              'text-purple-400',
                              'text-pink-400',
                              'text-yellow-400',
                              'text-red-400',
                              'text-indigo-400',
                              'text-teal-400'
                            ]
                            const colorIndicador = coloresIndicador[index % coloresIndicador.length]
                            return <Star className={`w-8 h-8 ${colorIndicador}`} />
                          })()}
                          <h3 className="text-2xl font-bold text-white">Premio de Pago Inmediato #{index + 1}</h3>
                        </div>
                        <div className="text-4xl font-bold text-blue-400 mb-2">{premio.premio}</div>
                        <div className="text-gray-400">{premio.numero_boletos} números ganadores</div>
                      </div>
                      
                      <div className="text-center text-gray-300">
                        {numerosSeleccionados[index] && numerosSeleccionados[index].length > 0 ? (
                          <div>
                            <p className="text-lg mb-3">Números ganadores:</p>
                            <div className="flex flex-wrap justify-center gap-2">
                              {numerosSeleccionados[index].map((numero, numIndex) => {
                                // Colores diferenciados por premio secundario
                                const colores = [
                                  'bg-gradient-to-r from-blue-500 to-blue-600 text-white',
                                  'bg-gradient-to-r from-green-500 to-green-600 text-white',
                                  'bg-gradient-to-r from-purple-500 to-purple-600 text-white',
                                  'bg-gradient-to-r from-pink-500 to-pink-600 text-white',
                                  'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white',
                                  'bg-gradient-to-r from-red-500 to-red-600 text-white',
                                  'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white',
                                  'bg-gradient-to-r from-teal-500 to-teal-600 text-white'
                                ]
                                const colorClase = colores[index % colores.length]
                                
                                return (
                                  <span
                                    key={numIndex}
                                    className={`${colorClase} px-3 py-1 rounded-full text-sm font-bold shadow-lg transform hover:scale-105 transition-transform`}
                                  >
                                    {numero.toString().padStart(5, '0')}
                                  </span>
                                )
                              })}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg">¡{premio.numero_boletos} boletos serán premiados!</p>
                            <p className="text-sm mt-2">Los números ganadores se anunciarán el día de la membresía</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
          </motion.div>

          {/* Sección de Compra de Paquetes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Adquiere tu <span className="gradient-text">Membresía</span>
              </h2>
              <p className="text-xl text-gray-300">
                Elige el paquete que más te convenga y aumenta tus posibilidades de ganar
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`card-modern p-8 relative cursor-pointer transition-all duration-300 ${
                    pkg.popular ? 'ring-2 ring-blue-400 scale-105' : 'hover:scale-105'
                  } ${
                    selectedPackage === pkg.id ? 'ring-2 ring-green-400' : ''
                  }`}
                  onClick={() => setSelectedPackage(pkg.id)}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                      MÁS POPULAR
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className="text-6xl font-bold text-white mb-4">{pkg.quantity}</div>
                    <div className="text-gray-400 mb-6">membresías {pkg.discount}%</div>
                    
                    <div className="mb-6">
                      <div className="text-4xl font-bold text-blue-400">
                        ${pkg.price.toLocaleString()} COP
                      </div>
                    </div>
                    
                    <div className="text-gray-400 text-sm mb-6">
                      ${(pkg.price / pkg.quantity).toFixed(2)} por membresía
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-full py-4 rounded-lg font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
                        selectedPackage === pkg.id
                          ? 'bg-green-500 text-white'
                          : 'btn-primary'
                      }`}
                    >
                      <ShoppingCart className="w-5 h-5" />
                      {selectedPackage === pkg.id ? 'Seleccionado' : 'Seleccionar'}
                    </motion.button>
                  </div>
                </motion.div>
              ))}
              
              {/* Opción Personalizada */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className={`card-modern p-8 relative cursor-pointer transition-all duration-300 ${
                  selectedPackage === 'custom' ? 'ring-2 ring-purple-400 scale-105' : 'hover:scale-105'
                }`}
                onClick={() => setSelectedPackage('custom')}
              >
                <div className="text-center">
                  <div className="text-6xl font-bold text-purple-400 mb-4">
                    <Plane className="w-16 h-16 mx-auto" />
                  </div>
                  <div className="text-gray-400 mb-6">adiciona mas números a tu membresía, entre mas números adiciones, mas oportunidades de Ganar! (no aplica modificación descuentos)</div>
                  
                  <div className="mb-6">
                    <label className="block text-sm text-gray-300 mb-2">Número de membresías</label>
                    <input
                      type="number"
                      min="1"
                      max={availableTickets}
                      value={customQuantity}
                      onChange={(e) => handleCustomQuantityChange(e.target.value)}
                      placeholder="Ej: 7"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Disponibles: {availableTickets.toLocaleString()}
                    </p>
                  </div>
                  
                  {customQuantity && (
                    <div className="mb-6">
                      <div className="text-4xl font-bold text-purple-400">
                        ${customTotal.toLocaleString()} COP
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        ${(customTotal / parseInt(customQuantity)).toFixed(2)} por membresía
                      </div>
                    </div>
                  )}
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-full py-4 rounded-lg font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
                      selectedPackage === 'custom'
                        ? 'bg-purple-500 text-white'
                        : 'btn-primary'
                    }`}
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {selectedPackage === 'custom' ? 'Seleccionado' : 'Seleccionar'}
                  </motion.button>
                </div>
              </motion.div>
            </div>

            {/* Botón de Compra Final */}
            {selectedPackage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center mt-12"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={selectedPackage === 'custom' && !isCustomQuantityValid}
                  onClick={() => setShowPaymentModal(true)}
                  className={`font-bold py-6 px-12 rounded-xl text-xl shadow-2xl transition-all duration-300 ${
                    selectedPackage === 'custom' && !isCustomQuantityValid
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-green-500/25'
                  }`}
                >
                  Proceder al Pago - ${
                    selectedPackage === 'custom' 
                      ? customTotal.toLocaleString()
                      : packages.find(p => p.id === selectedPackage)?.price.toLocaleString()
                  }
                </motion.button>
                <p className="text-gray-400 mt-4">
                  Pago seguro con tarjeta de crédito, débito o transferencia bancaria
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Sorteos Relacionados */}
        <section className="py-16 relative z-10">
          <div className="max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-white mb-4">Otras Membresías Activas</h2>
              <p className="text-gray-300 text-lg">Descubre más oportunidades de ganar</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {loadingRelated ? (
                // Loading state
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="card-modern animate-pulse">
                    <div className="bg-gray-700 h-48 rounded-lg mb-4"></div>
                    <div className="p-6">
                      <div className="bg-gray-700 h-6 rounded mb-2"></div>
                      <div className="bg-gray-700 h-4 rounded mb-4"></div>
                      <div className="bg-gray-700 h-8 rounded"></div>
                    </div>
                  </div>
                ))
              ) : relatedRaffles.length > 0 ? (
                relatedRaffles.map((relatedRaffle, index) => (
                  <motion.div
                    key={relatedRaffle.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="card-modern group cursor-pointer"
                    onClick={() => navigate(`/membresia/${relatedRaffle.id}`)}
                  >
                    <div className="relative overflow-hidden rounded-lg mb-4">
                      <img
                        src={relatedRaffle.image_url || "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=800&h=600&fit=crop&crop=center"}
                        alt={relatedRaffle.name}
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold">
                          ACTIVO
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{relatedRaffle.name}</h3>
                      <p className="text-gray-300 text-sm mb-4">{relatedRaffle.description || "Participa en esta increíble membresía"}</p>
                      
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <span className="text-2xl font-bold text-blue-400">${relatedRaffle.amount?.toLocaleString()} COP</span>
                          <span className="text-gray-400 text-sm block">por membresía</span>
                        </div>
                        <div className="text-right">
                          <span className="text-orange-400 font-bold">Activo</span>
                          <span className="text-gray-400 text-sm block">disponible</span>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex justify-between text-sm text-gray-300 mb-2">
                          <span>Vendidos: {(relatedRaffle.boletas_vendidas || 0).toLocaleString()}</span>
                          <span>{Math.round(((relatedRaffle.boletas_vendidas || 0) / (relatedRaffle.total_boletas || 100000)) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-pink-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${((relatedRaffle.boletas_vendidas || 0) / (relatedRaffle.total_boletas || 100000)) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    
                    <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105">
                      Ver Membresía
                    </button>
                  </div>
                </motion.div>
                ))
              ) : (
                <div className="col-span-full text-center text-gray-400 py-8">
                  <p>No hay otras membresías activas en este momento</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      
      {/* Modal de Pago */}
      {showPaymentModal && (
        <PaymentModal
           isOpen={showPaymentModal}
           onClose={() => setShowPaymentModal(false)}
           raffle={raffle}
           selectedPackage={selectedPackage}
           customQuantity={customQuantity}
           packages={packages}
         />
      )}
      
      {/* Botón flotante de WhatsApp */}
      <motion.a
        href="https://wa.me/573001234567?text=Hola,%20necesito%20información%20sobre%20las%20membresías"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300 group"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <MessageCircle className="w-6 h-6" />
        <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          ¿Necesitas ayuda? ¡Escríbenos!
        </div>
      </motion.a>
    </div>
  )
}

export default RafflePage